import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { CustomSelect } from '../../../shared/components/CustomSelect';
import {
  CalendarCheck,
  CalendarRange,
  ChevronDown,
  Flag,
  Info,
  LucideIcon,
  Rocket,
  Search,
  Trash2,
  Zap,
} from 'lucide-react';
import { dateUtils } from '../../../shared/utils';
import {
  AnalyticsPeriod,
  PeriodOptions,
  ReleaseOption,
  SprintOption,
  SprintPickerMeta,
} from './types';
import {
  comboboxFilterQuery,
  releaseMatchesFilter,
  sprintMatchesFilter,
} from './periodPickerFilter';
import './PeriodPicker.css';

type Mode = AnalyticsPeriod['mode'];

interface PeriodPickerProps {
  value: AnalyticsPeriod | null;
  onChange: (period: AnalyticsPeriod | null) => void;
  options: PeriodOptions;
  loading?: boolean;
  /** Sprints load from the default board saved under Releases → Sprint Plan. */
  sprintPickerMeta?: SprintPickerMeta;
  /**
   * Modes that are disabled (e.g. no sprints available yet). Empty disabled
   * modes render with a hint rather than being hidden to avoid layout jumps.
   */
  disabledModes?: Mode[];
}

/** Top row: current period; bottom row: previous period (yesterday / last week / last month). */
const PRESET_ROWS: Array<Array<{ key: string; label: string }>> = [
  [
    { key: 'today', label: 'Today' },
    { key: 'thisWeek', label: 'This Week' },
    { key: 'thisMonth', label: 'This Month' },
  ],
  [
    { key: 'yesterday', label: 'Yesterday' },
    { key: 'lastWeek', label: 'Last Week' },
    { key: 'lastMonth', label: 'Last Month' },
  ],
];

const MODE_META: Record<Mode, { label: string; hint: string; icon: LucideIcon }> = {
  dateRange: {
    label: 'Date range',
    hint: 'Quick presets: top row = current period (today / this week / this month), bottom row = previous (yesterday / last week / last month). Or set Start/End + Apply.',
    icon: CalendarRange,
  },
  sprint: {
    label: 'Sprint',
    hint: 'Sprints come from your default board: Releases → Sprint Plan → Save as default board. Dates are from Jira.',
    icon: Zap,
  },
  release: {
    label: 'Release',
    hint: "All worklogs between this release's start and end dates. Dates come from the Release Calendar on Releases.",
    icon: Rocket,
  },
  quarter: {
    label: 'Quarter',
    hint: 'Calendar quarter (Q1-Q4) based on issue created date. Useful for leadership reporting.',
    icon: Flag,
  },
};

const DEFAULT_SPRINT_META: SprintPickerMeta = {
  hasSavedDefaultBoard: false,
  listError: null,
};

function formatSprintOptionLabel(s: SprintOption): string {
  const statePart = s.state ? `[${s.state}] ` : '';
  const countPart = s.issueCount != null ? ` — ${s.issueCount} issues` : '';
  return `${statePart}${s.name}${countPart}`;
}

function formatReleaseOptionLabel(r: ReleaseOption): string {
  const storyPart = r.storyCount != null ? ` — ${r.storyCount} stories` : '';
  const issuePart = r.issueCount != null ? ` — ${r.issueCount} issues` : '';
  return `${r.fixVersion}${storyPart}${issuePart}`;
}

export const PeriodPicker: React.FC<PeriodPickerProps> = ({
  value,
  onChange,
  options,
  loading,
  sprintPickerMeta = DEFAULT_SPRINT_META,
  disabledModes = [],
}) => {
  const mode: Mode = value?.mode ?? 'dateRange';

  // Local draft state for dateRange so typing doesn't spam the URL.
  const initialStart = value?.mode === 'dateRange' ? value.startDate : '';
  const initialEnd = value?.mode === 'dateRange' ? value.endDate : '';

  const [draftStart, setDraftStart] = useState<string>(initialStart);
  const [draftEnd, setDraftEnd] = useState<string>(initialEnd);

  const [sprintInputValue, setSprintInputValue] = useState('');
  const [sprintListOpen, setSprintListOpen] = useState(false);
  const [sprintHighlight, setSprintHighlight] = useState(0);
  const sprintComboRef = useRef<HTMLDivElement>(null);
  /** When true, do not overwrite the input from URL/sync (user is filtering). */
  const sprintTypingRef = useRef(false);

  const [releaseInputValue, setReleaseInputValue] = useState('');
  const [releaseListOpen, setReleaseListOpen] = useState(false);
  const [releaseHighlight, setReleaseHighlight] = useState(0);
  const releaseComboRef = useRef<HTMLDivElement>(null);
  const releaseTypingRef = useRef(false);

  useEffect(() => {
    if (value?.mode === 'dateRange') {
      setDraftStart(value.startDate);
      setDraftEnd(value.endDate);
    }
  }, [value]);

  const sortedSprints = useMemo(() => {
    const prio: Record<string, number> = { closed: 0, active: 1, future: 2 };
    return [...(options.sprints || [])].sort((a, b) => {
      const pa = a.state ? (prio[a.state] ?? 3) : 3;
      const pb = b.state ? (prio[b.state] ?? 3) : 3;
      if (pa !== pb) return pa - pb;
      if (pa === 2) {
        const sa = a.startDate || '';
        const sb = b.startDate || '';
        return sa.localeCompare(sb);
      }
      const ea = a.endDate || '';
      const eb = b.endDate || '';
      return eb.localeCompare(ea);
    });
  }, [options.sprints]);

  const defaultSprintPick = useMemo(() => {
    const active = sortedSprints.find((s) => s.state === 'active');
    return active ?? sortedSprints[0];
  }, [sortedSprints]);

  useEffect(() => {
    if (value?.mode !== 'sprint') return;
    if (value.sprintId) return;
    if (!defaultSprintPick) return;
    onChange({
      mode: 'sprint',
      sprintId: defaultSprintPick.id,
      sprintName: defaultSprintPick.name,
    });
  }, [value, value?.mode === 'sprint' ? value.sprintId : undefined, defaultSprintPick, onChange]);

  useEffect(() => {
    if (mode !== 'sprint') {
      sprintTypingRef.current = false;
      setSprintInputValue('');
      setSprintListOpen(false);
      return;
    }
    if (sprintTypingRef.current) return;
    const id = value?.mode === 'sprint' ? value.sprintId : '';
    if (!id) {
      setSprintInputValue('');
      return;
    }
    const s = sortedSprints.find((x) => x.id === id);
    if (s) setSprintInputValue(formatSprintOptionLabel(s));
  }, [mode, value?.mode === 'sprint' ? value.sprintId : '', sortedSprints]);

  const restoreSprintInput = () => {
    sprintTypingRef.current = false;
    const id = value?.mode === 'sprint' ? value.sprintId : '';
    const s = id ? sortedSprints.find((x) => x.id === id) : undefined;
    if (s) setSprintInputValue(formatSprintOptionLabel(s));
  };

  const filteredSprints = useMemo(
    () =>
      sortedSprints.filter((s) =>
        sprintMatchesFilter(s, comboboxFilterQuery(sprintTypingRef.current, sprintInputValue))
      ),
    [sortedSprints, sprintInputValue, sprintListOpen]
  );

  useEffect(() => {
    setSprintHighlight((h) => {
      if (filteredSprints.length === 0) return 0;
      return Math.min(Math.max(0, h), filteredSprints.length - 1);
    });
  }, [filteredSprints.length, sprintInputValue]);

  useEffect(() => {
    if (!sprintListOpen) return;
    const onDocMouseDown = (e: MouseEvent) => {
      if (sprintComboRef.current?.contains(e.target as Node)) return;
      setSprintListOpen(false);
      restoreSprintInput();
    };
    document.addEventListener('mousedown', onDocMouseDown);
    return () => document.removeEventListener('mousedown', onDocMouseDown);
  }, [sprintListOpen, value, sortedSprints]);

  const sortedReleases = useMemo(() => [...(options.releases || [])], [options.releases]);

  useEffect(() => {
    if (mode !== 'release') {
      releaseTypingRef.current = false;
      setReleaseInputValue('');
      setReleaseListOpen(false);
      return;
    }
    if (releaseTypingRef.current) return;
    const fv = value?.mode === 'release' ? value.fixVersion : '';
    if (!fv) {
      setReleaseInputValue('');
      return;
    }
    const r = sortedReleases.find((x) => x.fixVersion === fv);
    if (r) setReleaseInputValue(formatReleaseOptionLabel(r));
  }, [mode, value?.mode === 'release' ? value.fixVersion : '', sortedReleases]);

  const restoreReleaseInput = () => {
    releaseTypingRef.current = false;
    const fv = value?.mode === 'release' ? value.fixVersion : '';
    const r = fv ? sortedReleases.find((x) => x.fixVersion === fv) : undefined;
    if (r) setReleaseInputValue(formatReleaseOptionLabel(r));
  };

  const filteredReleases = useMemo(
    () =>
      sortedReleases.filter((r) =>
        releaseMatchesFilter(r, comboboxFilterQuery(releaseTypingRef.current, releaseInputValue))
      ),
    [sortedReleases, releaseInputValue, releaseListOpen]
  );

  useEffect(() => {
    setReleaseHighlight((h) => {
      if (filteredReleases.length === 0) return 0;
      return Math.min(Math.max(0, h), filteredReleases.length - 1);
    });
  }, [filteredReleases.length, releaseInputValue]);

  useEffect(() => {
    if (!releaseListOpen) return;
    const onDocMouseDown = (e: MouseEvent) => {
      if (releaseComboRef.current?.contains(e.target as Node)) return;
      setReleaseListOpen(false);
      restoreReleaseInput();
    };
    document.addEventListener('mousedown', onDocMouseDown);
    return () => document.removeEventListener('mousedown', onDocMouseDown);
  }, [releaseListOpen, value, sortedReleases]);

  const pickRelease = (r: ReleaseOption) => {
    releaseTypingRef.current = false;
    onChange({ mode: 'release', fixVersion: r.fixVersion });
    setReleaseInputValue(formatReleaseOptionLabel(r));
    setReleaseListOpen(false);
  };

  const pickSprint = (s: SprintOption) => {
    sprintTypingRef.current = false;
    onChange({ mode: 'sprint', sprintId: s.id, sprintName: s.name });
    setSprintInputValue(formatSprintOptionLabel(s));
    setSprintListOpen(false);
  };

  const selectMode = (next: Mode) => {
    if (disabledModes.includes(next)) return;
    if (next === 'dateRange') {
      onChange({
        mode: 'dateRange',
        startDate: draftStart,
        endDate: draftEnd,
      });
      return;
    }
    if (next === 'sprint') {
      const pick = defaultSprintPick;
      if (pick) onChange({ mode: 'sprint', sprintId: pick.id, sprintName: pick.name });
      else onChange({ mode: 'sprint', sprintId: '', sprintName: '' });
      return;
    }
    if (next === 'release') {
      const first = sortedReleases[0];
      if (first) onChange({ mode: 'release', fixVersion: first.fixVersion });
      else onChange({ mode: 'release', fixVersion: '' });
      return;
    }
    if (next === 'quarter') {
      const first = options.quarters[0];
      if (first) onChange({ mode: 'quarter', quarter: first.id });
      else onChange({ mode: 'quarter', quarter: '' });
    }
  };

  const applyDateRange = () => {
    if (draftStart && draftEnd && draftStart <= draftEnd) {
      onChange({ mode: 'dateRange', startDate: draftStart, endDate: draftEnd });
    }
  };

  const clear = () => {
    setDraftStart('');
    setDraftEnd('');
    onChange(null);
  };

  const applyPreset = (key: string) => {
    const { start, end } = dateUtils.getDateRange(key);
    if (!start || !end) return;
    setDraftStart(start);
    setDraftEnd(end);
    onChange({ mode: 'dateRange', startDate: start, endDate: end, presetKey: key });
  };

  return (
    <section
      id="analytics-period-anchor"
      className="analytics-period-bar"
      aria-label="Analytics period selector"
    >
      <header className="analytics-period-bar__header">
        <h2 className="analytics-period-bar__title">
          <CalendarCheck size={18} aria-hidden />
          <span>Period</span>
          <span
            className="analytics-period-bar__title-hint"
            title="Controls all Analytics tabs. Every chart and table below re-scopes to the selected period."
            aria-label="Period selector hint"
          >
            <Info size={14} />
          </span>
        </h2>
        {loading ? <span className="analytics-period-bar__loading">Loading options…</span> : null}
      </header>

      <div className="analytics-period-bar__modes" role="tablist" aria-label="Period mode">
        {(Object.keys(MODE_META) as Mode[]).map((m) => {
          const meta = MODE_META[m];
          const Icon = meta.icon;
          const active = m === mode;
          const disabled = disabledModes.includes(m);
          return (
            <button
              key={m}
              type="button"
              role="tab"
              aria-selected={active}
              className={`period-mode-btn${active ? ' period-mode-btn--active' : ''}`}
              onClick={() => selectMode(m)}
              disabled={disabled}
              title={meta.hint}
            >
              <Icon size={16} />
              <span>{meta.label}</span>
            </button>
          );
        })}
      </div>

      <div className="analytics-period-bar__body">
        {mode === 'dateRange' && (
          <div className="period-body period-body--date">
            <div className="date-inputs">
              <div className="input-group">
                <label htmlFor="period-from">Start</label>
                <input
                  type="date"
                  id="period-from"
                  value={draftStart}
                  onChange={(e) => setDraftStart(e.target.value)}
                  title="Period start"
                />
              </div>
              <div className="input-group">
                <label htmlFor="period-to">End</label>
                <input
                  type="date"
                  id="period-to"
                  value={draftEnd}
                  onChange={(e) => setDraftEnd(e.target.value)}
                  title="Period end"
                />
              </div>
            </div>
            <div className="period-body__actions">
              <button
                type="button"
                className="btn btn-primary"
                onClick={applyDateRange}
                disabled={!draftStart || !draftEnd}
                title="Apply selected date range"
              >
                <Search size={16} />
                Apply
              </button>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={clear}
                title="Clear period filter"
              >
                <Trash2 size={16} />
                Clear
              </button>
            </div>
            <div className="quick-filters">
              <h4 className="quick-filters__heading">Quick presets</h4>
              <div className="period-presets-rows">
                {PRESET_ROWS.map((row, rowIndex) => (
                  <div key={rowIndex} className="filter-buttons-grid">
                    {row.map((p) => (
                      <button
                        key={p.key}
                        type="button"
                        className="btn btn-secondary quick-filter-btn"
                        onClick={() => applyPreset(p.key)}
                        title={`Switch to ${p.label.toLowerCase()}`}
                      >
                        <CalendarCheck size={16} />
                        <span className="quick-filter-btn__label">{p.label}</span>
                      </button>
                    ))}
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {mode === 'sprint' && (
          <div className="period-body period-body--select">
            {sortedSprints.length === 0 ? (
              sprintPickerMeta.listError ? (
                <p className="period-body__empty">{sprintPickerMeta.listError}</p>
              ) : !sprintPickerMeta.hasSavedDefaultBoard ? (
                <p className="period-body__empty period-sprint-hint">
                  Go to{' '}
                  <Link to="/releases" className="period-sprint-hint__link">
                    Releases
                  </Link>
                  → <strong>Sprint Plan</strong> → choose a board →{' '}
                  <strong>Save as default board</strong>. Then return to Analytics.
                </p>
              ) : (
                <p className="period-body__empty">
                  No sprints returned for this board. Check the board in Jira or pick another
                  default on Sprint Plan.
                </p>
              )
            ) : (
              <div className="input-group input-group--wide period-sprint-combobox-wrap">
                <label htmlFor="period-sprint">Sprint</label>
                <div
                  ref={sprintComboRef}
                  className={`period-sprint-combobox${sprintListOpen ? ' period-sprint-combobox--open' : ''}`}
                >
                  <div className="period-sprint-combobox__field">
                    <input
                      id="period-sprint"
                      type="text"
                      className="period-sprint-combobox__input"
                      role="combobox"
                      aria-expanded={sprintListOpen}
                      aria-controls="period-sprint-listbox"
                      aria-autocomplete="list"
                      autoComplete="off"
                      spellCheck={false}
                      placeholder="Type to filter (e.g. 13.04.26)…"
                      value={sprintInputValue}
                      onChange={(e) => {
                        sprintTypingRef.current = true;
                        setSprintInputValue(e.target.value);
                        setSprintListOpen(true);
                        setSprintHighlight(0);
                      }}
                      onFocus={() => setSprintListOpen(true)}
                      onKeyDown={(e) => {
                        if (e.key === 'Escape') {
                          e.preventDefault();
                          setSprintListOpen(false);
                          restoreSprintInput();
                          return;
                        }
                        if (e.key === 'ArrowDown') {
                          e.preventDefault();
                          if (!sprintListOpen) setSprintListOpen(true);
                          else if (filteredSprints.length > 0) {
                            setSprintHighlight((h) => (h + 1) % filteredSprints.length);
                          }
                          return;
                        }
                        if (e.key === 'ArrowUp') {
                          e.preventDefault();
                          if (!sprintListOpen) setSprintListOpen(true);
                          else if (filteredSprints.length > 0) {
                            setSprintHighlight(
                              (h) => (h - 1 + filteredSprints.length) % filteredSprints.length
                            );
                          }
                          return;
                        }
                        if (e.key === 'Enter' && sprintListOpen && filteredSprints.length > 0) {
                          e.preventDefault();
                          const s = filteredSprints[sprintHighlight];
                          if (s) pickSprint(s);
                        }
                      }}
                    />
                    <button
                      type="button"
                      className="period-sprint-combobox__chevron"
                      aria-label={sprintListOpen ? 'Close sprint list' : 'Open sprint list'}
                      tabIndex={-1}
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => {
                        if (sprintListOpen) {
                          restoreSprintInput();
                          setSprintListOpen(false);
                          return;
                        }
                        setSprintHighlight(0);
                        setSprintListOpen(true);
                      }}
                    >
                      <ChevronDown size={18} aria-hidden />
                    </button>
                  </div>
                  {sprintListOpen ? (
                    <ul
                      id="period-sprint-listbox"
                      role="listbox"
                      className="period-sprint-combobox__list"
                    >
                      {filteredSprints.length === 0 ? (
                        <li className="period-sprint-combobox__empty" role="presentation">
                          No sprints match your search.
                        </li>
                      ) : (
                        filteredSprints.map((s, i) => (
                          <li
                            key={s.id}
                            role="option"
                            aria-selected={i === sprintHighlight}
                            className={`period-sprint-combobox__option${
                              i === sprintHighlight
                                ? ' period-sprint-combobox__option--highlight'
                                : ''
                            }`}
                            onMouseEnter={() => setSprintHighlight(i)}
                            onMouseDown={(e) => e.preventDefault()}
                            onClick={() => pickSprint(s)}
                          >
                            {formatSprintOptionLabel(s)}
                          </li>
                        ))
                      )}
                    </ul>
                  ) : null}
                </div>
              </div>
            )}
          </div>
        )}

        {mode === 'release' && (
          <div className="period-body period-body--select">
            {options.releases.length === 0 ? (
              <div className="period-release-empty-block">
                <p className="period-body__empty">
                  No fix versions found. Check that issues are tagged with a release in Jira.
                </p>
                <p className="period-release-scope-hint" role="note">
                  Analytics will include all worklogs between a release&apos;s{' '}
                  <strong>start</strong> and <strong>end</strong> dates. Those dates are on the
                  Release Calendar under{' '}
                  <Link to="/releases" className="period-sprint-hint__link">
                    Releases
                  </Link>
                  .
                </p>
              </div>
            ) : (
              <div className="input-group input-group--wide period-sprint-combobox-wrap">
                <label htmlFor="period-release">Release</label>
                <div
                  ref={releaseComboRef}
                  className={`period-sprint-combobox${releaseListOpen ? ' period-sprint-combobox--open' : ''}`}
                >
                  <div className="period-sprint-combobox__field">
                    <input
                      id="period-release"
                      type="text"
                      className="period-sprint-combobox__input"
                      role="combobox"
                      aria-expanded={releaseListOpen}
                      aria-controls="period-release-listbox"
                      aria-autocomplete="list"
                      autoComplete="off"
                      spellCheck={false}
                      placeholder="Type to filter (e.g. WBE 1.26)…"
                      value={releaseInputValue}
                      onChange={(e) => {
                        releaseTypingRef.current = true;
                        setReleaseInputValue(e.target.value);
                        setReleaseListOpen(true);
                        setReleaseHighlight(0);
                      }}
                      onFocus={() => setReleaseListOpen(true)}
                      onKeyDown={(e) => {
                        if (e.key === 'Escape') {
                          e.preventDefault();
                          setReleaseListOpen(false);
                          restoreReleaseInput();
                          return;
                        }
                        if (e.key === 'ArrowDown') {
                          e.preventDefault();
                          if (!releaseListOpen) setReleaseListOpen(true);
                          else if (filteredReleases.length > 0) {
                            setReleaseHighlight((h) => (h + 1) % filteredReleases.length);
                          }
                          return;
                        }
                        if (e.key === 'ArrowUp') {
                          e.preventDefault();
                          if (!releaseListOpen) setReleaseListOpen(true);
                          else if (filteredReleases.length > 0) {
                            setReleaseHighlight(
                              (h) => (h - 1 + filteredReleases.length) % filteredReleases.length
                            );
                          }
                          return;
                        }
                        if (e.key === 'Enter' && releaseListOpen && filteredReleases.length > 0) {
                          e.preventDefault();
                          const r = filteredReleases[releaseHighlight];
                          if (r) pickRelease(r);
                        }
                      }}
                    />
                    <button
                      type="button"
                      className="period-sprint-combobox__chevron"
                      aria-label={releaseListOpen ? 'Close release list' : 'Open release list'}
                      tabIndex={-1}
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => {
                        if (releaseListOpen) {
                          restoreReleaseInput();
                          setReleaseListOpen(false);
                          return;
                        }
                        setReleaseHighlight(0);
                        setReleaseListOpen(true);
                      }}
                    >
                      <ChevronDown size={18} aria-hidden />
                    </button>
                  </div>
                  {releaseListOpen ? (
                    <ul
                      id="period-release-listbox"
                      role="listbox"
                      className="period-sprint-combobox__list"
                    >
                      {filteredReleases.length === 0 ? (
                        <li className="period-sprint-combobox__empty" role="presentation">
                          No releases match your search.
                        </li>
                      ) : (
                        filteredReleases.map((r, i) => (
                          <li
                            key={r.fixVersion}
                            role="option"
                            aria-selected={i === releaseHighlight}
                            className={`period-sprint-combobox__option${
                              i === releaseHighlight
                                ? ' period-sprint-combobox__option--highlight'
                                : ''
                            }`}
                            onMouseEnter={() => setReleaseHighlight(i)}
                            onMouseDown={(e) => e.preventDefault()}
                            onClick={() => pickRelease(r)}
                          >
                            {formatReleaseOptionLabel(r)}
                          </li>
                        ))
                      )}
                    </ul>
                  ) : null}
                </div>
                <p className="period-release-scope-hint" role="note">
                  {value?.mode === 'release' && value.fixVersion ? (
                    <>
                      All worklogs between this release&apos;s <strong>start</strong> and{' '}
                      <strong>end</strong> dates. Find those dates in the Release Calendar on the{' '}
                      <Link to="/releases" className="period-sprint-hint__link">
                        Releases
                      </Link>{' '}
                      page.
                    </>
                  ) : (
                    <>
                      Choose a release: analytics will include all worklogs between that
                      release&apos;s <strong>start</strong> and <strong>end</strong> dates (see the
                      calendar on{' '}
                      <Link to="/releases" className="period-sprint-hint__link">
                        Releases
                      </Link>
                      ).
                    </>
                  )}
                </p>
              </div>
            )}
          </div>
        )}

        {mode === 'quarter' && (
          <div className="period-body period-body--select">
            {options.quarters.length === 0 ? (
              <p className="period-body__empty">No quarters computed yet. Sync Jira data first.</p>
            ) : (
              <div className="input-group input-group--wide">
                <label htmlFor="period-quarter">Quarter</label>
                <CustomSelect
                  id="period-quarter"
                  value={value?.mode === 'quarter' ? value.quarter : ''}
                  options={options.quarters.map((q) => ({
                    value: q.id,
                    label: q.issueCount != null ? `${q.label} — ${q.issueCount} issues` : q.label,
                  }))}
                  onChange={(val) => onChange({ mode: 'quarter', quarter: val })}
                />
              </div>
            )}
          </div>
        )}
      </div>
    </section>
  );
};

export default PeriodPicker;
